import { Fragment, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { dcGinProgress, dcLineGinCode, dcLinePendingQty, dcLineReceivedQty, fmtQty, hasPendingGinLines, isDcLineReceived, pendingDcLineIdxs } from "@/lib/dcGinProgress";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { Plus, CheckCircle2, XCircle, ClipboardCheck, Trash2, Truck, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import GinGrnSearchBar, { SEARCH_FIELDS } from "@/components/GinGrnSearchBar";
import { exportGinsExcel } from "@/lib/procurementExports";
import ReceivedPartPicker from "@/components/ReceivedPartPicker";

const STATUS = {
  pending_inspection: { cls: "bg-amber-50 text-amber-700", label: "Pending QC" },
  approved: { cls: "bg-emerald-50 text-emerald-700", label: "Approved" },
  partial: { cls: "bg-blue-50 text-blue-700", label: "Partial" },
  rejected: { cls: "bg-red-50 text-red-700", label: "Rejected" },
};

export default function GoodsInwardPage() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [list, setList] = useState([]);
  const [items, setItems] = useState([]);
  const [stores, setStores] = useState([]);
  const [pos, setPos] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [dcs, setDcs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  // Active Search criteria ({ part_no } / { supplier }, or {} for all GINs).
  const [search, setSearch] = useState({});
  const searchActive = Object.keys(search).length > 0;

  // Job Work DCs dispatched with qty still to be received on at least one
  // line (sent qty − qty already received on earlier GINs > 0) — a DC
  // received in part stays here for the next GIN until its full qty is
  // back. The pick-list for the
  // "Against Job Work DC" dropdown in New GIN below. As soon as a DC is
  // created and sent out (Production Flow's "Create Job Work DC"), it shows
  // up here by its own DC number, ready to pull in once the material comes back.
  const receivableDcs = useMemo(
    () => dcs.filter((d) => d.kind === "subcontract" && d.status === "dispatched" && hasPendingGinLines(d)),
    [dcs]
  );

  const canInspect = ["admin", "quality", "quality_inspector"].includes(user?.role);

  const [form, setForm] = useState({
    po_id: "", dc_id: "", supplier: "", supplier_id: "", vehicle_no: "", invoice_no: "", invoice_date: "",
    gate_pass_no: "", notes: "",
    lines: [{ item_id: "", received_qty: 0, target_store: "" }],
  });
  const dcTarget = receivableDcs.find((d) => d.id === form.dc_id) || null;
  // Inspection state per line (when a GIN is selected)
  const [inspectLines, setInspectLines] = useState([]);

  const itemMap = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const storeMap = useMemo(() => Object.fromEntries(stores.map((s) => [s.code, s.name])), [stores]);

  const load = async (params = search) => {
    const [g, i, s, p, sup, dc] = await Promise.all([
      api.get("/gins", { params }), api.get("/items"), api.get("/stores"), api.get("/purchase-orders"),
      api.get("/suppliers?status=active").catch(() => ({ data: [] })),
      api.get("/delivery-challans?kind=subcontract").catch(() => ({ data: [] })),
    ]);
    setList(g.data);
    setItems(i.data);
    setStores(s.data);
    setPos(p.data.filter((po) => po.status === "open"));
    setSuppliers(sup.data || []);
    setDcs(dc.data || []);
    if (selected) {
      const found = g.data.find((x) => x.id === selected.id);
      setSelected(found || null);
      if (found) setInspectLines(found.lines.map((l) => ({ ...l })));
    }
  };
  useEffect(() => { load(); }, []);

  const runSearch = async (params) => {
    try { await load(params); setSearch(params); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Search failed"); }
  };

  const exportList = () => {
    try {
      exportGinsExcel({ gins: list, itemMap, storeMap });
      toast.success(`Exported ${list.length} GIN(s)`);
    } catch { toast.error("Export failed"); }
  };

  const onSelect = (g) => {
    setSelected(g);
    setInspectLines(g.lines.map((l) => ({ ...l })));
  };

  // Deep-link support: /goods-inward?gin=<id> (e.g. from the Quality Inspections
  // page's "Pending Goods Inward" queue) auto-selects that GIN once the list loads.
  useEffect(() => {
    const focusId = searchParams.get("gin");
    if (!focusId || list.length === 0) return;
    const found = list.find((g) => g.id === focusId);
    if (found) onSelect(found);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("gin");
      return next;
    }, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list]);

  const addLine = () => setForm((f) => ({ ...f, lines: [...f.lines, { item_id: items[0]?.id || "", received_qty: 0, target_store: "" }] }));
  const setLine = (i, p) => setForm((f) => ({ ...f, lines: f.lines.map((l, idx) => idx === i ? { ...l, ...p } : l) }));
  const rmLine = (i) => setForm((f) => ({ ...f, lines: f.lines.filter((_, idx) => idx !== i) }));

  const pickPO = async (poId) => {
    const po = pos.find((p) => p.id === poId);
    if (!po) return setForm((f) => ({ ...f, po_id: "" }));
    const lines = (po.lines || []).map((l) => {
      const item = itemMap[l.item_id];
      const def = { FG: "FG_STORE", SFG: "SFG_STORE", RAW: "RM_STORE" }[item?.type] || "RM_STORE";
      return { item_id: l.item_id, received_qty: l.qty, target_store: def };
    });
    setForm((f) => ({ ...f, po_id: poId, dc_id: "", supplier: po.supplier || "", lines }));
  };

  // Picking a Job Work DC lists what was sent out on it (item + qty sent)
  // straight into the Lines table so the GIN can be raised in one step —
  // no separate "Receive Job Work DC" flow needed. Each line then needs its
  // Received Part Number (stage after job work); that part's stage decides
  // the store the QC-accepted qty is credited to.
  const pickDc = (dcId) => {
    const dc = receivableDcs.find((d) => d.id === dcId);
    if (!dc) return setForm((f) => ({ ...f, dc_id: "" }));
    // One row per DC line with qty still remaining, pre-filled with that
    // remaining qty (sent − already received on earlier GINs) — a Job Work
    // DC can be received over several GINs; fully-received lines are left
    // out, and a row left at 0 stays pending on the DC.
    setForm((f) => ({
      ...f,
      dc_id: dcId,
      po_id: "",
      supplier: dc.vendor_name || "",
      supplier_id: null,
      lines: pendingDcLineIdxs(dc).map((idx) => {
        const line = dc.lines[idx];
        return {
          dc_line_idx: idx,
          item_id: line.item_id,
          received_item_id: "",
          // Blank = the received part's stage store; overridable per line.
          target_store: "",
          // Manual (one-time) DC item — no Items master entry; carried by
          // its part description / part number instead, with no store.
          is_manual: !!line.is_manual && !line.item_id,
          part_code: line.part_code || "",
          part_name: line.part_name || "",
          pending_qty: dcLinePendingQty(line),
          received_qty: dcLinePendingQty(line),
        };
      }),
    }));
  };

  const create = async () => {
    if (busy) return;
    const lines = form.lines.filter((l) => (l.item_id || l.is_manual) && l.received_qty > 0);
    if (!lines.length) return toast.error("Add at least one line with qty");
    const over = form.dc_id && lines.find((l) => l.received_qty > l.pending_qty + 1e-6);
    if (over) return toast.error(`${over.part_code || "Line"}: received qty exceeds the remaining qty ${fmtQty(over.pending_qty)}`);
    const noPart = form.dc_id && lines.find((l) => !l.is_manual && !l.received_item_id);
    if (noPart) return toast.error(`${noPart.part_code || "Line"}: select the Received Part Number (stage after job work)`);
    setBusy(true);
    try {
      if (form.dc_id) {
        // Job Work DC selected — receive against it directly (creates the
        // GIN and links it to the DC's received lines; the DC closes out as
        // delivered only once every line has a GIN).
        await api.post(`/delivery-challans/${form.dc_id}/receive-goods`, {
          lines: lines.map((l) => ({
            dc_line_idx: l.dc_line_idx,
            received_qty: l.received_qty,
            received_item_id: l.is_manual ? null : l.received_item_id,
            target_store: l.is_manual ? "" : (l.target_store || ""),
          })),
          notes: form.notes,
        });
      } else {
        await api.post("/gins", {
          po_id: form.po_id || null,
          supplier: form.supplier,
          supplier_id: form.supplier_id || null,
          vehicle_no: form.vehicle_no, invoice_no: form.invoice_no,
          invoice_date: form.invoice_date ? new Date(form.invoice_date).toISOString() : null,
          gate_pass_no: form.gate_pass_no, notes: form.notes, lines,
        });
      }
      toast.success("Goods Inward Note created — awaiting QC inspection");
      setOpen(false);
      setForm({ po_id: "", dc_id: "", supplier: "", supplier_id: "", vehicle_no: "", invoice_no: "", invoice_date: "", gate_pass_no: "", notes: "", lines: [{ item_id: items[0]?.id || "", received_qty: 0, target_store: "" }] });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Create failed"); }
    finally { setBusy(false); }
  };

  const setInspLine = (idx, patch) => setInspectLines((ls) => ls.map((l, i) => i === idx ? { ...l, ...patch } : l));

  const saveInspect = async () => {
    setBusy(true);
    try {
      // Validate
      for (const l of inspectLines) {
        if ((l.accepted_qty || 0) + (l.rejected_qty || 0) > l.received_qty + 1e-6) {
          toast.error("Accepted + rejected cannot exceed received");
          setBusy(false); return;
        }
      }
      await api.post(`/gins/${selected.id}/inspect`, { lines: inspectLines });
      toast.success("Inspection saved");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  const approve = async () => {
    setBusy(true);
    try {
      // First save inspection
      for (const l of inspectLines) {
        if ((l.accepted_qty || 0) + (l.rejected_qty || 0) > l.received_qty + 1e-6) {
          toast.error("Accepted + rejected cannot exceed received");
          setBusy(false); return;
        }
      }
      await api.post(`/gins/${selected.id}/inspect`, { lines: inspectLines });
      const { data } = await api.post(`/gins/${selected.id}/approve`);
      toast.success(`Approved · ${data.grn.code} created · stock credited`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Approve failed"); }
    finally { setBusy(false); }
  };

  const reject = async () => {
    if (!window.confirm("Reject all material in this GIN?")) return;
    setBusy(true);
    try {
      await api.post(`/gins/${selected.id}/reject`);
      toast.success("GIN rejected · no stock credited");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Reject failed"); }
    finally { setBusy(false); }
  };

  const remove = async (g) => {
    if (!window.confirm(`Delete ${g.code}?`)) return;
    try { await api.delete(`/gins/${g.id}`); if (selected?.id === g.id) setSelected(null); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="gin-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Inward</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Goods Inward Notes
          </h1>
          <p className="text-sm text-slate-500 mt-1">Material at gate → Quality inspects → on approval converts to GRN and credits the target store.</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={exportList} disabled={list.length === 0} data-testid="gin-export-list-btn"
            title="Export the GINs listed below (respects the active search) to Excel">
            <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export
          </Button>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button className="bg-blue-600 hover:bg-blue-700" data-testid="new-gin-btn"><Plus className="w-4 h-4 mr-1.5" />New GIN</Button>
            </SheetTrigger>
          <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
            <SheetHeader><SheetTitle>New Goods Inward Note</SheetTitle></SheetHeader>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Against Purchase Order (optional)</Label>
                <Select value={form.po_id || "__none__"} onValueChange={(v) => pickPO(v === "__none__" ? "" : v)} disabled={!!form.dc_id}>
                  <SelectTrigger data-testid="gin-po-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None / Manual —</SelectItem>
                    {pos.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} · {p.supplier}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Against Job Work DC (optional)</Label>
                <Select value={form.dc_id || "__none__"} onValueChange={(v) => (v === "__none__" ? setForm((f) => ({ ...f, dc_id: "" })) : pickDc(v))} disabled={!!form.po_id}>
                  <SelectTrigger data-testid="gin-dc-select"><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— None —</SelectItem>
                    {receivableDcs.map((d) => (
                      <SelectItem key={d.id} value={d.id} data-testid={`gin-dc-opt-${d.code}`}>
                        {d.code} · {d.vendor_name || "Vendor"} · {d.stage_label || "Raw Material"}
                        {dcGinProgress(d).anyReceived && ` · ${fmtQty(dcGinProgress(d).pendingQty)} of ${fmtQty(dcGinProgress(d).sentQty)} qty pending`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {receivableDcs.length === 0 && (
                  <p className="text-[11px] text-slate-500 mt-1">Nothing waiting — every Job Work DC has already been received.</p>
                )}
                {dcTarget && (
                  <div className="mt-2 grid grid-cols-2 gap-2 text-[11px] bg-orange-50 border border-orange-200 rounded p-2" data-testid="gin-dc-details">
                    {(dcTarget.lines || []).map((l, idx) => (
                      <Fragment key={idx}>
                        <div><span className="text-slate-500">Item:</span> <span className="mono font-semibold" data-testid={`gin-dc-detail-item-${idx}`}>{l.part_code || "—"}</span></div>
                        <div>
                          <span className="text-slate-500">Sent Qty:</span> <span className="mono font-semibold">{l.qty}</span>
                          {isDcLineReceived(dcTarget, l) ? (
                            <span className="ml-1 text-emerald-700">· fully received on <span className="mono">{dcLineGinCode(dcTarget, l)}</span></span>
                          ) : dcLineReceivedQty(l) > 0 && (
                            <span className="ml-1 text-amber-700" data-testid={`gin-dc-detail-remaining-${idx}`}>
                              · received {fmtQty(dcLineReceivedQty(l))} on <span className="mono">{dcLineGinCode(dcTarget, l)}</span> · remaining {fmtQty(dcLinePendingQty(l))}
                            </span>
                          )}
                        </div>
                      </Fragment>
                    ))}
                    <div><span className="text-slate-500">Vendor:</span> <strong>{dcTarget.vendor_name || "—"}</strong></div>
                    <div><span className="text-slate-500">Stage:</span> <strong>{dcTarget.stage_label || "Raw Material"}</strong></div>
                  </div>
                )}
              </div>
              {form.dc_id ? (
                // Job Work DC selected — the DC already fixes the vendor and
                // items, so the Lines table mirrors the manual/PO one but with
                // item-locked rows (one per DC line): Received Qty and the
                // Received Part Number are entered here; the Target Store
                // defaults to that part's stage store but can be overridden. (See POST
                // /delivery-challans/{id}/receive-goods.)
                <div>
                  <div className="text-sm font-semibold text-slate-700 mb-2">Lines</div>
                  <div className="space-y-2">
                    {form.lines.map((ln, idx) => (
                      <div key={idx} className="grid grid-cols-12 gap-2 items-end border border-slate-200 rounded p-2" data-testid={`gin-dc-line-${idx}`}>
                        <div className="col-span-8">
                          <Label className="text-[10px] uppercase">Sent Part Number (on DC)</Label>
                          <Input
                            disabled
                            value={`${itemMap[ln.item_id]?.code || ln.part_code || "—"} — ${itemMap[ln.item_id]?.name || ln.part_name || ""}`}
                          />
                        </div>
                        <div className="col-span-4">
                          <Label className="text-[10px] uppercase">Received Qty * <span className="normal-case text-slate-500">(max {fmtQty(ln.pending_qty)})</span></Label>
                          <Input
                            type="number"
                            min="0"
                            max={ln.pending_qty}
                            step="0.01"
                            value={ln.received_qty ?? ""}
                            onChange={(e) => setLine(idx, { received_qty: parseFloat(e.target.value || 0) })}
                            data-testid={idx === 0 ? "gin-dc-qty-input" : `gin-dc-qty-input-${idx}`}
                          />
                        </div>
                        <div className="col-span-12">
                          {ln.is_manual ? (
                            <>
                              <Label className="text-[10px] uppercase">Received Part Number</Label>
                              <Input disabled value="One-time item — no stock" data-testid={`gin-dc-manual-store-${idx}`} />
                            </>
                          ) : (
                            <ReceivedPartPicker
                              value={ln.received_item_id}
                              items={items}
                              storeMap={storeMap}
                              onPick={(v) => setLine(idx, { received_item_id: v })}
                              targetStore={ln.target_store}
                              onTargetStore={(v) => setLine(idx, { target_store: v })}
                              testId={idx === 0 ? "gin-dc-received-part" : `gin-dc-received-part-${idx}`}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Supplier</Label>
                      <Select
                        value={form.supplier_id || "__manual"}
                        onValueChange={(v) => {
                          if (v === "__manual") {
                            setForm({ ...form, supplier_id: "", supplier: form.supplier });
                          } else {
                            const s = suppliers.find((x) => x.id === v);
                            setForm({ ...form, supplier_id: v, supplier: s?.name || "" });
                          }
                        }}
                      >
                        <SelectTrigger data-testid="gin-supplier-select"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="__manual">— Manual entry —</SelectItem>
                          {suppliers.map((s) => (
                            <SelectItem key={s.id} value={s.id}>{s.code} · {s.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {!form.supplier_id && (
                        <Input
                          className="mt-1.5"
                          placeholder="Type supplier name..."
                          value={form.supplier}
                          onChange={(e) => setForm({ ...form, supplier: e.target.value })}
                          data-testid="gin-supplier-text"
                        />
                      )}
                      {suppliers.length === 0 && (
                        <div className="text-[11px] text-amber-700 mt-1">
                          No suppliers in master. <a href="/suppliers" className="underline">Add or import suppliers →</a>
                        </div>
                      )}
                    </div>
                    <div><Label>Invoice No.</Label><Input value={form.invoice_no} onChange={(e) => setForm({ ...form, invoice_no: e.target.value })} /></div>
                    <div><Label>Invoice Date</Label><Input type="date" value={form.invoice_date} onChange={(e) => setForm({ ...form, invoice_date: e.target.value })} /></div>
                    <div><Label>Vehicle No.</Label><Input value={form.vehicle_no} onChange={(e) => setForm({ ...form, vehicle_no: e.target.value })} data-testid="gin-vehicle-input" /></div>
                    <div><Label>Gate Pass No.</Label><Input value={form.gate_pass_no} onChange={(e) => setForm({ ...form, gate_pass_no: e.target.value })} /></div>
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-slate-700">Lines</div>
                      <Button size="sm" variant="outline" onClick={addLine}><Plus className="w-3.5 h-3.5 mr-1" />Add</Button>
                    </div>
                    <div className="space-y-2">
                      {form.lines.map((ln, idx) => (
                        <div key={idx} className="grid grid-cols-12 gap-2 items-end">
                          <div className="col-span-5">
                            <Label className="text-[10px] uppercase">Item</Label>
                            <Select value={ln.item_id} onValueChange={(v) => setLine(idx, { item_id: v })}>
                              <SelectTrigger><SelectValue /></SelectTrigger>
                              <SelectContent>
                                {items.map((i) => <SelectItem key={i.id} value={i.id}>{i.code} — {i.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase">Received Qty</Label>
                            <Input type="number" value={ln.received_qty} onChange={(e) => setLine(idx, { received_qty: parseFloat(e.target.value || 0) })} />
                          </div>
                          <div className="col-span-3">
                            <Label className="text-[10px] uppercase">Target Store</Label>
                            <Select value={ln.target_store} onValueChange={(v) => setLine(idx, { target_store: v })}>
                              <SelectTrigger><SelectValue placeholder="Auto" /></SelectTrigger>
                              <SelectContent>
                                {stores.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                              </SelectContent>
                            </Select>
                          </div>
                          <div className="col-span-1"><Button variant="ghost" size="icon" onClick={() => rmLine(idx)}><Trash2 className="w-4 h-4 text-red-500" /></Button></div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
              <div>
                <Label>Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} />
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={create} disabled={busy} data-testid="create-gin-btn">Create GIN</Button>
            </SheetFooter>
          </SheetContent>
          </Sheet>
        </div>
      </div>

      <GinGrnSearchBar onSearch={runSearch} active={searchActive} testIdPrefix="gin" />

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg overflow-hidden">
          <div className="px-4 py-3 border-b border-slate-100 text-sm font-semibold text-slate-700 flex justify-between gap-2">
            <span className="truncate">
              {searchActive
                ? <>Search results · {SEARCH_FIELDS[Object.keys(search)[0]]}: <span className="mono">{Object.values(search)[0]}</span></>
                : "Inward Notes"}
            </span>
            <span className="text-xs text-slate-500 mono">{list.length}</span>
          </div>
          <div className="divide-y divide-slate-100 max-h-[70vh] overflow-y-auto scroll-thin">
            {list.length === 0 && (searchActive
              ? <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="gin-no-results">No records found.</div>
              : <div className="px-4 py-10 text-center text-sm text-slate-500" data-testid="no-gins">No goods inward notes yet.</div>)}
            {list.map((g) => {
              const meta = STATUS[g.status] || STATUS.pending_inspection;
              return (
                <button key={g.id} onClick={() => onSelect(g)} data-testid={`gin-row-${g.code}`}
                  className={`w-full text-left px-4 py-3 hover:bg-slate-50 transition ${selected?.id === g.id ? "bg-blue-50/60" : ""}`}>
                  <div className="flex items-center justify-between">
                    <div className="mono text-xs text-slate-500 uppercase tracking-[0.14em]">{g.code}</div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>{meta.label}</span>
                  </div>
                  <div className="mt-1 text-sm font-medium text-slate-800">{g.supplier || "Manual"}</div>
                  <div className="text-xs text-slate-500">
                    {g.dc_code
                      ? <span className="mono text-orange-700">{g.dc_code}</span>
                      : g.po_code ? <span className="mono text-blue-700">{g.po_code}</span> : "Manual receipt"}
                    {" "}· {g.lines?.length || 0} line(s){g.vehicle_no ? ` · ${g.vehicle_no}` : ""}
                  </div>
                  {searchActive && (
                    <div className="text-[11px] text-slate-500 mono truncate">
                      {(g.lines || []).map((l) => itemMap[l.item_id]?.code || l.part_code || l.part_name).filter(Boolean).join(", ")}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="lg:col-span-3 bg-white border border-slate-200 rounded-lg p-6 min-h-[60vh]" data-testid="gin-detail">
          {!selected ? (
            <div className="h-full flex flex-col items-center justify-center text-sm text-slate-500 gap-2">
              <Truck className="w-10 h-10 text-slate-300" /> Select a GIN to inspect.
            </div>
          ) : (
            <div className="space-y-5">
              <div className="flex items-start justify-between">
                <div>
                  <div className="text-xs uppercase tracking-[0.14em] text-slate-500">{selected.code}</div>
                  <h2 className="text-2xl font-extrabold tracking-tight text-slate-900" style={{ fontFamily: "Manrope" }}>
                    {selected.supplier || "Manual receipt"}
                  </h2>
                  <div className="text-xs text-slate-500 mt-1">
                    {selected.dc_code ? <>Against Job Work DC <span className="mono text-orange-700">{selected.dc_code}</span> · </> : null}
                    {selected.po_code ? <>Against PO <span className="mono text-blue-700">{selected.po_code}</span> · </> : null}
                    {selected.invoice_no ? <>Inv {selected.invoice_no} · </> : null}
                    {selected.vehicle_no ? <>Vehicle <span className="mono">{selected.vehicle_no}</span></> : null}
                  </div>
                  {selected.grn_code && (
                    <div className="text-xs text-emerald-700 mt-1">→ Converted to <span className="mono font-semibold">{selected.grn_code}</span></div>
                  )}
                </div>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${STATUS[selected.status]?.cls}`}>{STATUS[selected.status]?.label}</span>
              </div>

              {selected.status === "pending_inspection" && canInspect && (
                <div className="flex items-center gap-2 flex-wrap" data-testid="gin-actions">
                  <Button onClick={approve} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="gin-approve-btn">
                    <CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & Create GRN
                  </Button>
                  <Button variant="outline" onClick={saveInspect} disabled={busy} data-testid="gin-save-insp-btn">
                    <ClipboardCheck className="w-4 h-4 mr-1.5" /> Save Inspection
                  </Button>
                  <Button variant="outline" onClick={reject} disabled={busy} data-testid="gin-reject-btn">
                    <XCircle className="w-4 h-4 mr-1.5 text-red-500" /> Reject All
                  </Button>
                  <Button variant="ghost" size="icon" onClick={() => remove(selected)} className="ml-auto"><Trash2 className="w-4 h-4 text-red-500" /></Button>
                </div>
              )}
              {selected.status !== "pending_inspection" && selected.inspected_by && (
                <div className="text-xs text-slate-500">
                  Inspected by <span className="font-medium text-slate-700">{selected.inspected_by}</span>
                  {selected.inspected_at ? ` on ${new Date(selected.inspected_at).toLocaleString()}` : ""}
                </div>
              )}

              <div>
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Lines</div>
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
                      <th className="py-2 px-3">Item</th>
                      <th className="py-2 px-3">Received</th>
                      <th className="py-2 px-3">Accepted</th>
                      <th className="py-2 px-3">Rejected</th>
                      <th className="py-2 px-3">Target Store</th>
                      <th className="py-2 px-3">Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(selected.status === "pending_inspection" && canInspect ? inspectLines : selected.lines).map((l, idx) => {
                      const editable = selected.status === "pending_inspection" && canInspect;
                      return (
                        <tr key={idx} className="border-t border-slate-100">
                          <td className="py-2 px-3 mono">
                            {itemMap[l.item_id]?.code || l.part_code || (l.is_manual ? "—" : "")}{" "}
                            <span className="text-slate-500 text-xs">— {itemMap[l.item_id]?.name || l.part_name}</span>
                            {l.is_manual && <span className="ml-1 text-[10px] uppercase tracking-wider text-amber-700" data-testid={`gin-line-manual-${idx}`}>one-time</span>}
                            {l.received_item_id && (
                              <div className="text-[11px] text-slate-500 font-sans" data-testid={`gin-line-received-part-${idx}`}>
                                Received Part Number{l.sent_item_code && l.sent_item_code !== l.received_item_code ? <> · sent as <span className="mono">{l.sent_item_code}</span></> : null}
                              </div>
                            )}
                          </td>
                          <td className="py-2 px-3 mono">{l.received_qty}</td>
                          <td className="py-2 px-3">
                            {editable ? (
                              <Input type="number" className="w-24" value={l.accepted_qty || 0} onChange={(e) => setInspLine(idx, { accepted_qty: Math.max(0, parseFloat(e.target.value || 0)) })} data-testid={`accepted-${idx}`} />
                            ) : <span className="mono font-semibold text-emerald-700">{l.accepted_qty}</span>}
                          </td>
                          <td className="py-2 px-3">
                            {editable ? (
                              <Input type="number" className="w-24" value={l.rejected_qty || 0} onChange={(e) => setInspLine(idx, { rejected_qty: Math.max(0, parseFloat(e.target.value || 0)) })} data-testid={`rejected-${idx}`} />
                            ) : <span className="mono font-semibold text-red-700">{l.rejected_qty}</span>}
                          </td>
                          <td className="py-2 px-3">
                            {l.is_manual ? <span className="text-xs text-slate-500">No stock (one-time item)</span> : editable ? (
                              <Select value={l.target_store || ""} onValueChange={(v) => setInspLine(idx, { target_store: v })}>
                                <SelectTrigger className="h-8"><SelectValue placeholder="Pick store" /></SelectTrigger>
                                <SelectContent>
                                  {stores.map((s) => <SelectItem key={s.code} value={s.code}>{s.name}</SelectItem>)}
                                </SelectContent>
                              </Select>
                            ) : <span className="text-xs">{storeMap[l.target_store] || l.target_store}</span>}
                          </td>
                          <td className="py-2 px-3">
                            {editable ? (
                              <Input value={l.remarks || ""} onChange={(e) => setInspLine(idx, { remarks: e.target.value })} className="h-8" />
                            ) : <span className="text-xs text-slate-600">{l.remarks || "—"}</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {selected.notes && (
                <div className="text-sm text-slate-600 border-l-2 border-blue-300 pl-3 italic">{selected.notes}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
